import { useState } from 'react';
import { Cable } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useMidiState } from '@/hooks/useMidi';
import { midiNoteName } from '@/lib/midi';

const selectCls = 'h-11 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-3 text-sm text-white focus:outline-none focus:border-pink-400';
const Label = ({ children }) => <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">{children}</span>;

const CLOCK_OPTIONS = [
  { v: 'off', label: 'Off' },
  { v: 'send', label: 'Invia tempo' },
  { v: 'receive', label: 'Segui tempo esterno' },
];

const MidiBody = ({ m }) => {
  const s = m.settings;
  return (
    <div className="flex flex-col gap-5">
      <label className="flex flex-col gap-2">
        <Label>Ingresso (riceve)</Label>
        <select data-testid="midi-input-select" className={selectCls} value={s.inputId} onChange={(e) => m.update({ inputId: e.target.value })}>
          <option value="all">Tutti i dispositivi</option>
          {m.inputs.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </label>

      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col"><Label>Ricevi Start / Stop</Label><span className="text-xs text-white/50 mt-1">Play e Stop dal dispositivo avviano il metronomo</span></div>
        <Switch data-testid="midi-receive-transport" checked={s.receiveTransport} onCheckedChange={(v) => m.update({ receiveTransport: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <Label>Tasto / pad Play-Stop</Label>
        <div className="flex items-center gap-2">
          <div data-testid="midi-trigger-note" className="h-11 flex-1 rounded-xl bg-[#0A0A0A] border border-white/10 px-3 flex items-center font-mono text-sm">
            {m.learning ? 'Premi un tasto o pad…' : s.triggerNote === null ? 'Nessuno' : `${midiNoteName(s.triggerNote)} (${s.triggerNote})`}
          </div>
          <button data-testid="midi-learn-btn" onClick={() => m.setLearning(!m.learning)} className={`h-11 px-4 rounded-xl text-xs uppercase tracking-[0.2em] active:scale-95 transition-transform ${m.learning ? 'bg-pink-400 text-black animate-pulse' : 'border border-white/15 hover:bg-white/5'}`}>
            {m.learning ? 'Annulla' : 'Impara'}
          </button>
          {s.triggerNote !== null && !m.learning && (
            <button data-testid="midi-trigger-clear" onClick={() => m.update({ triggerNote: null })} className="h-11 px-3 rounded-xl border border-white/15 text-xs uppercase tracking-[0.2em] hover:bg-white/5">Rimuovi</button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Clock MIDI</Label>
        <div className="grid grid-cols-3 gap-1.5">
          {CLOCK_OPTIONS.map((o) => (
            <button key={o.v} data-testid={`midi-clock-${o.v}`} onClick={() => m.update({ clockMode: o.v })} className={`h-11 rounded-xl text-[11px] uppercase tracking-[0.1em] transition-colors ${s.clockMode === o.v ? 'bg-pink-400 text-black' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}>
              {o.label}
            </button>
          ))}
        </div>
        {s.clockMode === 'receive' && (
          <span data-testid="midi-external-bpm" className="text-xs text-white/50">Tempo esterno: {m.externalBpm ? `${m.externalBpm} BPM` : 'in attesa del clock…'}</span>
        )}
      </div>

      {s.clockMode === 'send' && (
        <label className="flex flex-col gap-2">
          <Label>Uscita (invia tempo e Start/Stop)</Label>
          <select data-testid="midi-output-select" className={selectCls} value={s.outputId} onChange={(e) => m.update({ outputId: e.target.value })}>
            <option value="">Scegli un dispositivo</option>
            {m.outputs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      )}

      <div data-testid="midi-device-count" className="text-[11px] text-white/40">
        {m.inputs.length} ingressi · {m.outputs.length} uscite collegati · nel Tuner gli altri tasti cambiano la nota del bordone
      </div>
      <button data-testid="midi-disable-btn" onClick={() => m.disable()} className="h-11 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] hover:bg-white/5">Disattiva MIDI</button>
    </div>
  );
};

const MidiIntro = ({ m }) => (
  <div className="flex flex-col gap-4">
    {!m.supported ? (
      <p data-testid="midi-unsupported" className="text-sm text-white/60">Questo browser non supporta il MIDI. Usa Chrome o Edge su computer o Android (iPhone e iPad non sono supportati).</p>
    ) : (
      <>
        <p className="text-sm text-white/60">Collega una tastiera o drum machine via USB o Bluetooth, poi attiva il MIDI.</p>
        {m.error && <p data-testid="midi-error" className="text-sm text-rose-300">{m.error === 'denied' ? 'Permesso MIDI negato dal browser.' : 'Impossibile accedere al MIDI.'}</p>}
        <button data-testid="midi-enable-btn" onClick={() => m.enable()} className="h-12 rounded-full bg-pink-400 text-black text-xs font-semibold uppercase tracking-[0.2em] hover:bg-pink-300 active:scale-95 transition-transform">Attiva MIDI</button>
      </>
    )}
  </div>
);

export const MidiButton = () => {
  const m = useMidiState();
  const [open, setOpen] = useState(false);
  const on = m.settings.enabled && !!m.access;
  return (
    <>
      <button
        data-testid="midi-open-btn"
        aria-label="MIDI"
        onClick={() => setOpen(true)}
        className={`relative shrink-0 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${on ? 'text-sky-300 bg-sky-400/10' : 'text-white/55 hover:text-white hover:bg-white/5'}`}
      >
        <Cable className="w-4 h-4" />
        {on && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-sky-400" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="midi-dialog" className="bg-[#111112] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bebas text-3xl tracking-wide">MIDI<span className="text-pink-400">.</span></DialogTitle>
            <DialogDescription className="text-white/50">Controlla Player e Sequencer da tastiere e drum machine.</DialogDescription>
          </DialogHeader>
          {on ? <MidiBody m={m} /> : <MidiIntro m={m} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
